import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { encode } from "gpt-tokenizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  submitHeadlinesJob,
  pollHeadlinesJob,
  JobExpired,
  AccessRevoked,
  JobFailed,
  PollingTimeout,
  PollingAborted,
} from "components/pages/Educator/Headlines/headlinesJobs";
import {
  DetailsLinkIcon,
  GlowIcon,
  GrowIcon,
  NextStepIcon,
  StatusIcon,
  StudentNameStarIcon,
} from "components/pages/Educator/Headlines/headlinesIcons";
import { logError, LogCategory } from "utils/sentryLogger";
import { saveFilters, resolveClassId } from "utils/persistFilters";
import { DateFilterDropdown, SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { useAvailableModels } from "hooks/useAvailableModels";
import { BadgeXSmall } from "components/shared/Badges/BadgeXSmall";
import {
  DebugToggleRow,
  FilterBar,
  FilterGroup,
  FilterLabel,
  GenerateBar,
  GenerateButton,
  LlmTuningPanel,
  StaleNote,
  TokenSummaryBox,
  TuningFieldFull,
  TuningHint,
} from "components/shared/LlmTuning/LlmTuning";
import { useSessionData } from "components/pages/Admin/CustomReport/hooks/useSessionData";
import { SessionFetcher } from "components/pages/Admin/CustomReport/SessionFetcher";
import { AnalysisFieldSelector } from "components/pages/Admin/CustomReport/AnalysisFieldSelector";
import { formatTokenCount } from "components/pages/Admin/CustomReport/formatters";

const LEVEL_DISPLAY_NAMES = {
  1: "Needs\nIntervention",
  2: "Needs\nGuidance",
  3: "Effective\nCollaborator",
  4: "Exceeding\nExpectations",
};

const nextDayIso = (iso) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const formatStarText = (text) => {
  if (!text) return text;
  return text.replace(/^Student Star:\s*/i, "");
};

// Strips LLM annotation tags like [CLIMATE:ANTI-COLLABORATIVE] from narrative
// text. Tag format: [UPPERCASE_LABEL:UPPERCASE_VALUE] (value may contain
// dashes/underscores). Also collapses leftover whitespace.
const stripBracketTags = (text) => {
  if (!text) return text;
  return text
    .replace(/\s*\[[A-Z][A-Z0-9_]*:[A-Z0-9_-]+\]\s*/g, " ")
    .replace(/ {2,}/g, " ")
    .replace(/ ([,.;:!?])/g, "$1")
    .trim();
};

export const CollaborationHeadlines = ({
  educatorId = null,
  teacherEmail = null,
  sharedClassId,
  onClassChange,
  onGoToCollaboration,
  adminUserData,
}) => {
  const isAdmin = Boolean(adminUserData?.can_access_user_admin);

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassIdInternal] = useState(null);
  const setSelectedClassId = (id) => {
    setSelectedClassIdInternal(id);
    if (onClassChange) onClassChange(id);
  };
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedDateMode, setSelectedDateMode] = useState("weekly");

  const [headlines, setHeadlines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  const { provider, setProvider, modelName, setModelName, modelOptions, modelsLoading } =
    useAvailableModels();
  const [temperature, setTemperature] = useState(0.3);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [debugMode, setDebugMode] = useState(false);
  const [thinkingLevel, setThinkingLevel] = useState("");
  const [sessionsPanelOpen, setSessionsPanelOpen] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const sessionData = useSessionData(teacherEmail, selectedClassId, { selectAll: true });
  const {
    analysisData,
    availableFields,
    selectedFields,
    selectedSessions,
    selectedSessionIds,
    estimatedTokenCount,
    setStartDate: setSessionsStart,
    setEndDate: setSessionsEnd,
  } = sessionData;

  const trimmedAnalysisData = useMemo(() => {
    if (!isAdmin || !analysisData || analysisData.length === 0) return null;
    const includedFields = availableFields.filter((f) => selectedFields[f]);
    if (includedFields.length === 0) return null;
    const filtered = analysisData
      .filter((item) => selectedSessions[item.session_id])
      .map((item) => {
        if (!item.analysis || typeof item.analysis !== "object") return null;
        const trimmed = {};
        includedFields.forEach((f) => {
          if (f in item.analysis) trimmed[f] = item.analysis[f];
        });
        return { session_id: item.session_id, analysis: trimmed };
      })
      .filter(Boolean);
    return filtered.length > 0 ? filtered : null;
  }, [isAdmin, analysisData, availableFields, selectedFields, selectedSessions]);

  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);
  const selectedClass = classes.find((c) => c.id === selectedClassId);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      saveFilters("collaboration_headlines", {
        selectedClassId,
        selectedWeek,
        selectedEndDate,
        selectedDateMode,
      });
    }
  }, [selectedClassId, selectedWeek, selectedEndDate, selectedDateMode]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        let classList;
        if (educatorId) {
          const data = await request(`class-report/educator/${educatorId}/classes`, {
            method: "GET",
          });
          classList = Array.isArray(data) ? data : data?.classes || [];
        } else {
          const educatorData = await request("account/educator", { method: "GET" });
          classList = educatorData?.classes || [];
        }
        setClasses(classList);
        if (classList.length > 0) {
          if (sharedClassId && classList.some((c) => c.id === sharedClassId)) {
            setSelectedClassId(sharedClassId);
          } else {
            const { classId, saved } = await resolveClassId("collaboration_headlines", classList);
            if (saved?.selectedWeek) setSelectedWeek(saved.selectedWeek);
            if (saved?.selectedEndDate) setSelectedEndDate(saved.selectedEndDate);
            if (saved?.selectedDateMode) setSelectedDateMode(saved.selectedDateMode);
            setSelectedClassId(classId);
          }
        }
      } catch (e) {
        logError("Failed to fetch collaboration headlines classes", e, {
          category: LogCategory.ERROR,
        });
        setError(e?.detail || e?.message || "An unknown error occurred.");
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educatorId]);

  const handleDatesLoaded = useCallback((weeks, days) => {
    setSelectedWeek((prev) => {
      if (prev) return prev;
      if (weeks.length > 0) {
        setSelectedDateMode("weekly");
        return weeks[0].week_of;
      }
      if (days.length > 0) {
        setSelectedDateMode("daily");
        return days[0].date;
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!isAdmin || !selectedWeek) return;
    let endDate;
    if (selectedDateMode === "daily") {
      endDate = selectedWeek;
    } else if (selectedDateMode === "custom" && selectedEndDate) {
      endDate = selectedEndDate;
    } else {
      const d = new Date(selectedWeek + "T00:00:00");
      d.setDate(d.getDate() + 6);
      endDate = d.toISOString().split("T")[0];
    }
    setSessionsStart(selectedWeek);
    setSessionsEnd(endDate);
  }, [isAdmin, selectedWeek, selectedEndDate, selectedDateMode, setSessionsStart, setSessionsEnd]);

  const analysisSignature = useMemo(() => {
    if (!trimmedAnalysisData) return "";
    const includedFields = availableFields.filter((f) => selectedFields[f]).sort();
    const sessionIds = trimmedAnalysisData.map((i) => i.session_id).sort();
    return `${sessionIds.join(",")}::${includedFields.join(",")}`;
  }, [trimmedAnalysisData, availableFields, selectedFields]);

  const cacheKey = useMemo(() => {
    if (!selectedClassId || !selectedWeek) return null;
    return [
      selectedClassId,
      selectedDateMode,
      selectedWeek,
      selectedEndDate || "",
      isAdmin ? provider || "" : "",
      isAdmin ? modelName || "" : "",
      isAdmin ? String(temperature) : "",
      isAdmin ? systemPrompt : "",
      isAdmin ? userPrompt : "",
      isAdmin ? analysisSignature : "",
      isAdmin && debugMode ? "debug" : "",
      isAdmin ? thinkingLevel : "",
    ].join("|");
  }, [
    selectedClassId,
    selectedWeek,
    selectedEndDate,
    selectedDateMode,
    isAdmin,
    provider,
    modelName,
    temperature,
    systemPrompt,
    userPrompt,
    thinkingLevel,
    analysisSignature,
    debugMode,
  ]);

  useEffect(() => {
    if (!cacheKey) return;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setHeadlines(cached);
      setError(null);
    }
  }, [cacheKey]);

  const handleGenerate = useCallback(async () => {
    if (!selectedClassId || !selectedWeek || !cacheKey) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setReportLoading(true);
    setHeadlines(null);
    setError(null);
    setHasGenerated(true);
    try {
      const payload = {};
      if (educatorId) payload.educator_id = educatorId;
      if (selectedDateMode === "daily") {
        payload.start_date = selectedWeek;
        payload.end_date = nextDayIso(selectedWeek);
      } else if (selectedDateMode === "custom" && selectedEndDate) {
        payload.start_date = selectedWeek;
        payload.end_date = selectedEndDate;
      } else {
        payload.week_of = selectedWeek;
      }
      if (isAdmin) {
        if (provider) payload.provider = provider;
        if (modelName) payload.model_name = modelName;
        if (temperature != null) payload.temperature = temperature;
        if (systemPrompt.trim()) payload.system_prompt = systemPrompt;
        if (userPrompt.trim()) payload.prompt = userPrompt;
        if (trimmedAnalysisData) payload.analysis_data = trimmedAnalysisData;
        if (debugMode) payload.debug = true;
        if (thinkingLevel) payload.thinking_level = thinkingLevel;
      }
      const { job_id } = await submitHeadlinesJob(
        selectedClassId,
        "collaboration",
        payload,
        controller.signal
      );
      const result = await pollHeadlinesJob(job_id, controller.signal);
      if (controller.signal.aborted) return;
      cacheRef.current.set(cacheKey, result);
      setHeadlines(result);
    } catch (e) {
      if (e instanceof PollingAborted || controller.signal.aborted) return;
      if (e instanceof JobExpired || e instanceof PollingTimeout) {
        setError(e.message);
      } else if (e instanceof AccessRevoked) {
        setError(e.message);
      } else if (e instanceof JobFailed) {
        setError(e.message);
      } else if (e?.status === 404) {
        setHeadlines(null);
      } else if (e?.status === 403) {
        setError("You don't have access to this classroom.");
      } else if (e?.status === 502) {
        setError("LLM provider temporarily unavailable. Please retry.");
      } else if (e?.status === 422) {
        setError(e?.detail || e?.message || "Invalid request.");
      } else {
        logError("Failed to fetch collaboration headlines", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load headlines.");
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (!controller.signal.aborted) setReportLoading(false);
    }
  }, [
    cacheKey,
    selectedClassId,
    selectedWeek,
    selectedEndDate,
    selectedDateMode,
    educatorId,
    isAdmin,
    provider,
    modelName,
    temperature,
    systemPrompt,
    userPrompt,
    trimmedAnalysisData,
    debugMode,
    thinkingLevel,
  ]);

  const canGenerate = Boolean(selectedClassId && selectedWeek && !reportLoading);
  const cachedForCurrent = cacheKey ? cacheRef.current.has(cacheKey) : false;
  const isStale = hasGenerated && !cachedForCurrent && !reportLoading;

  const promptTokenCount = useMemo(() => {
    if (!isAdmin) return 0;
    const combined = `${systemPrompt || ""}\n${userPrompt || ""}`.trim();
    return combined ? encode(combined).length : 0;
  }, [isAdmin, systemPrompt, userPrompt]);
  const contextTokenCount = trimmedAnalysisData ? estimatedTokenCount : 0;
  const sessionsInContext = trimmedAnalysisData ? trimmedAnalysisData.length : 0;

  if (loading) {
    return (
      <Container>
        <CardShell>
          <CollaborationHeadlinesSkeleton />
        </CardShell>
      </Container>
    );
  }

  return (
    <Container>
      <FilterBar>
        <FilterGroup>
          <FilterLabel>Class</FilterLabel>
          <SelectDropdown
            ariaLabel="Class"
            value={selectedClassId}
            displayText={classDisplayName(selectedClass) || "Select a class"}
            options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
            groupBySchoolYear
            onChange={setSelectedClassId}
          />
        </FilterGroup>

        <DateFilterGroup>
          <DateFilterDropdown
            label="Date"
            classroomId={selectedClassId}
            educatorId={educatorId}
            startDate={selectedWeek}
            endDate={selectedEndDate}
            selectedDateMode={selectedDateMode}
            onDatesLoaded={handleDatesLoaded}
            maxRangeDays={14}
            onDateChange={(start, end, mode) => {
              setSelectedWeek(start);
              setSelectedEndDate(end);
              setSelectedDateMode(mode);
            }}
          />
          <DateRangeNote>Custom range limited to 2 weeks · applies to this tab only</DateRangeNote>
        </DateFilterGroup>
      </FilterBar>

      {isAdmin && teacherEmail && (
        <SessionsSection>
          <SessionsHeader onClick={() => setSessionsPanelOpen((v) => !v)}>
            <span>
              Admin: LLM context sessions
              {trimmedAnalysisData && (
                <TuningHint>
                  {" "}· {trimmedAnalysisData.length} session{trimmedAnalysisData.length === 1 ? "" : "s"} ·{" "}
                  {formatTokenCount(estimatedTokenCount)} tokens
                </TuningHint>
              )}
              {!trimmedAnalysisData && (
                <TuningHint> (omit to use server-side DB fetch)</TuningHint>
              )}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                transform: sessionsPanelOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </SessionsHeader>
          {sessionsPanelOpen && (
            <SessionsBody>
              <SessionFetcher
                {...sessionData}
                hideDateInputs
                onFetchSessions={sessionData.handleFetchSessions}
                onSelectAll={sessionData.handleSelectAll}
                onToggleSession={sessionData.handleToggleSession}
              />
              {analysisData && availableFields.length > 0 && (
                <AnalysisFieldSelector
                  availableFields={availableFields}
                  selectedFields={selectedFields}
                  allFieldsSelected={sessionData.allFieldsSelected}
                  estimatedTokenCount={estimatedTokenCount}
                  selectedSessionCount={selectedSessionIds.length}
                  onToggleField={sessionData.handleToggleField}
                  onSelectAllFields={sessionData.handleSelectAllFields}
                  previewData={trimmedAnalysisData || []}
                />
              )}
            </SessionsBody>
          )}
        </SessionsSection>
      )}

      {isAdmin && (
        <LlmTuningPanel
          idPrefix="collab-headlines"
          provider={provider}
          modelName={modelName}
          modelOptions={modelOptions}
          modelsLoading={modelsLoading}
          temperature={temperature}
          thinkingLevel={thinkingLevel}
          systemPrompt={systemPrompt}
          userPrompt={userPrompt}
          onChange={(patch) => {
            if ("provider" in patch) setProvider(patch.provider);
            else if ("modelName" in patch) setModelName(patch.modelName);
            if ("temperature" in patch) setTemperature(patch.temperature);
            if ("thinkingLevel" in patch) setThinkingLevel(patch.thinkingLevel);
            if ("systemPrompt" in patch) setSystemPrompt(patch.systemPrompt);
            if ("userPrompt" in patch) setUserPrompt(patch.userPrompt);
          }}
          resetDisabled={!systemPrompt && !userPrompt}
          onResetPrompts={() => {
            setSystemPrompt("");
            setUserPrompt("");
          }}
        >
          <TuningFieldFull>
            <DebugToggleRow>
              <input
                type="checkbox"
                id="collab-headlines-debug-toggle"
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
              />
              <label htmlFor="collab-headlines-debug-toggle">
                <strong>Debug mode</strong>
                <TuningHint>
                  {" "}— skip the LLM, return the assembled prompt (no model spend, no latency).
                </TuningHint>
              </label>
            </DebugToggleRow>
          </TuningFieldFull>
        </LlmTuningPanel>
      )}

      <GenerateBar>
        {isAdmin && (
          <TokenSummaryBox
            rows={[
              { label: "Analysis data", tokens: contextTokenCount },
              { label: "Prompt", tokens: promptTokenCount },
            ]}
          />
        )}
        <GenerateButton type="button" disabled={!canGenerate} onClick={handleGenerate}>
          {reportLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
              Generating headlines...
            </>
          ) : debugMode ? (
            `Generate headlines (debug${sessionsInContext ? ` · ${sessionsInContext} session${sessionsInContext === 1 ? "" : "s"}` : ""})`
          ) : (
            `Generate headlines${sessionsInContext ? ` (${sessionsInContext} session${sessionsInContext === 1 ? "" : "s"})` : ""}`
          )}
        </GenerateButton>
        {isStale && <StaleNote>Inputs have changed — click Generate to refresh.</StaleNote>}
      </GenerateBar>

      <CardShell>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {!error && reportLoading && <CollaborationHeadlinesSkeleton />}
        {!error && !reportLoading && !headlines && hasGenerated && (
          <EmptyState>No collaboration data for this range.</EmptyState>
        )}
        {!error && !reportLoading && !headlines && !hasGenerated && (
          <EmptyState>
            {selectedWeek
              ? "Click Generate headlines to run the LLM call."
              : "Select a class and date range to begin."}
          </EmptyState>
        )}
        {!error && !reportLoading && headlines && (
          <CollaborationHeadlinesCard
            data={headlines}
            classId={selectedClassId}
            selectedWeek={selectedWeek}
            selectedEndDate={selectedEndDate}
            selectedDateMode={selectedDateMode}
            onGoToCollaboration={onGoToCollaboration}
          />
        )}
      </CardShell>

      {isAdmin && !reportLoading && headlines?.llm_input_debug && (
        <DebugPanel>
          <DebugPanelTitle>Debug: assembled LLM input</DebugPanelTitle>
          <DebugBlock>
            <DebugBlockLabel>System prompt</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.system_prompt || "(empty)"}</DebugPre>
          </DebugBlock>
          <DebugBlock>
            <DebugBlockLabel>Human prompt</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.human_prompt || "(empty)"}</DebugPre>
          </DebugBlock>
          <DebugBlock>
            <DebugBlockLabel>Context JSON</DebugBlockLabel>
            <DebugPre>{headlines.llm_input_debug.context_json || "(empty)"}</DebugPre>
          </DebugBlock>
        </DebugPanel>
      )}
    </Container>
  );
};

const CollaborationHeadlinesCard = ({
  data,
  classId,
  selectedWeek,
  selectedEndDate,
  selectedDateMode,
  onGoToCollaboration,
}) => {
  const studentStars = Array.isArray(data.student_stars) ? data.student_stars : [];
  const collaborationStatus = Array.isArray(data.collaboration_status)
    ? data.collaboration_status
    : [];
  const criticalIncidents = Array.isArray(data.critical_incidents) ? data.critical_incidents : [];
  const starsCount = typeof data.stars_count === "number" ? data.stars_count : null;
  const glow = typeof data.glow === "string" ? data.glow : null;
  const grow = typeof data.grow === "string" ? data.grow : null;
  const nextStep = typeof data.next_step === "string" ? data.next_step : "";
  const isDebug = data.llm_input_debug != null;

  const weekEnd =
    selectedDateMode === "daily"
      ? nextDayIso(selectedWeek)
      : selectedDateMode === "custom" && selectedEndDate
        ? selectedEndDate
        : selectedWeek
          ? (() => {
              const d = new Date(selectedWeek + "T00:00:00");
              d.setDate(d.getDate() + 6);
              return d.toISOString().split("T")[0];
            })()
          : null;

  return (
    <>
      {studentStars.length > 0 && (
        <StudentStarsCarousel
          stars={studentStars}
          classId={classId}
          weekOf={selectedWeek}
          weekEnd={weekEnd}
        />
      )}

      {collaborationStatus.length > 0 && (
        <Section>
          <SectionHeader>
            <StatusIcon />
            <SectionTitle>Collaboration Status</SectionTitle>
          </SectionHeader>
          <CollabStatusRow>
            {collaborationStatus.map((lvl) => (
              <CollabStatusItem key={lvl.level}>
                <BadgeWrapper>
                  <BadgeXSmall level={lvl.level} />
                </BadgeWrapper>
                <CollabStatusText>
                  <CollabStatusLabel>
                    {LEVEL_DISPLAY_NAMES[lvl.level] || lvl.name}
                  </CollabStatusLabel>
                  <CollabStatusCount>
                    {lvl.student_count} {lvl.student_count === 1 ? "Student" : "Students"}
                  </CollabStatusCount>
                </CollabStatusText>
              </CollabStatusItem>
            ))}
          </CollabStatusRow>
        </Section>
      )}

      {/* TODO: pending design validation
      {criticalIncidents.length > 0 && (
        <CriticalIncidentsCarousel incidents={criticalIncidents} />
      )}
      */}

      {!isDebug && (
        <>
          {(glow || grow) && (
            <TwoColumn>
              <NarrativeBlock icon={<GlowIcon />} title="Glow" text={glow} />
              <NarrativeBlock icon={<GrowIcon />} title="Grow" text={grow} />
            </TwoColumn>
          )}
          <NarrativeBlock
            icon={<NextStepIcon />}
            title="Next Step"
            text={nextStep}
            asParagraph
          />
        </>
      )}

      <LinkButton as="a" href="#section-collaboration">
        <span>Go to Math Discourse and Collaboration Details section</span>
        <DetailsLinkIcon />
      </LinkButton>
    </>
  );
};

/**
 * Session Reports for one student star: the class and the week the headline
 * was written about, the flagged session when the star names one, and the
 * student highlighted in the list. Exported so the URL can be tested without
 * standing up the whole console, which needs a class, a week, the headline
 * job round trip and a click on Generate before a star exists at all.
 */
export const starReportLink = ({ star, classId, weekOf, weekEnd }) => {
  const params = new URLSearchParams();
  if (classId) params.set("classroom_id", classId);
  if (weekOf) params.set("start_date", weekOf);
  if (weekEnd) params.set("end_date", weekEnd);
  if (star?.session_id) params.set("session_id", star.session_id);
  if (star?.student_name) params.set("highlight_student", star.student_name);
  // Session Reports opens By Lesson by default; this link is about sessions,
  // including when the star names no session of its own.
  params.set("tab", "groups");
  return `${constants.SESSION_HISTORY_PATH}?${params.toString()}`;
};

const StudentStarsCarousel = ({ stars, classId, weekOf, weekEnd }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToPrevious = () =>
    setCurrentIndex((idx) => (idx > 0 ? idx - 1 : stars.length - 1));
  const goToNext = () =>
    setCurrentIndex((idx) => (idx < stars.length - 1 ? idx + 1 : 0));
  const star = stars[currentIndex];
  if (!star) return null;

  const handleCardClick = () => {
    if (!star.session_id && !classId) return;
    saveFilters("session_history", {
      classroomId: classId,
      ...(weekOf && { startDate: weekOf, endDate: weekEnd }),
    });
    window.open(starReportLink({ star, classId, weekOf, weekEnd }), "_blank");
  };

  return (
    <Section>
      <SectionHeader>
        <FontAwesomeIcon icon={faStar} style={{ fontSize: 20, color: colors.blue500 }} />
        <SectionTitle>Student Stars</SectionTitle>
        {stars.length > 0 && (
          <StarsCountPill>
            {stars.length} Collaboration Star{stars.length === 1 ? "" : "s"}
          </StarsCountPill>
        )}
      </SectionHeader>
      <CarouselContainer>
        <CarouselButton
          onClick={goToPrevious}
          disabled={stars.length <= 1}
          aria-label="Previous student"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </CarouselButton>
        <StarCard onClick={handleCardClick} $clickable={Boolean(star.session_id)}>
          <StarCardHeader>
            <StudentNameStarIcon />
            <StarStudentName>{star.student_name}</StarStudentName>
          </StarCardHeader>
          <StarText>{formatStarText(star.star_text)}</StarText>
        </StarCard>
        <CarouselButton
          onClick={goToNext}
          disabled={stars.length <= 1}
          aria-label="Next student"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </CarouselButton>
      </CarouselContainer>
    </Section>
  );
};

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" stroke="#605DEC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CriticalIncidentsCarousel = ({ incidents }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToPrevious = () =>
    setCurrentIndex((idx) => (idx > 0 ? idx - 1 : incidents.length - 1));
  const goToNext = () =>
    setCurrentIndex((idx) => (idx < incidents.length - 1 ? idx + 1 : 0));
  const incident = incidents[currentIndex];
  if (!incident) return null;

  return (
    <Section>
      <SectionHeader>
        <AlertIcon />
        <SectionTitle>Critical Incidents</SectionTitle>
        <StarsCountPill>
          {incidents.length} Incident{incidents.length === 1 ? "" : "s"}
        </StarsCountPill>
      </SectionHeader>
      <CarouselContainer>
        <CarouselButton
          onClick={goToPrevious}
          disabled={incidents.length <= 1}
          aria-label="Previous incident"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </CarouselButton>
        <IncidentCard>
          <IncidentMeta>
            <IncidentStudentName>{incident.student_name}</IncidentStudentName>
            {incident.question_number != null && (
              <IncidentQuestionLabel>Question {incident.question_number}</IncidentQuestionLabel>
            )}
          </IncidentMeta>
          <IncidentQuote>"{incident.quote}"</IncidentQuote>
        </IncidentCard>
        <CarouselButton
          onClick={goToNext}
          disabled={incidents.length <= 1}
          aria-label="Next incident"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </CarouselButton>
      </CarouselContainer>
    </Section>
  );
};

const NarrativeBlock = ({ icon, title, text, asParagraph = false }) => {
  if (!text) return null;
  const cleaned = stripBracketTags(text).replace(/\*\*/g, "").trim();
  if (!cleaned) return null;
  const sentences = cleaned
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const asList = !asParagraph && sentences.length > 1;
  return (
    <Section>
      <SectionHeader>
        {icon}
        <SectionTitle>{title}</SectionTitle>
      </SectionHeader>
      {asList ? (
        <NarrativeList>
          {sentences.map((s, i) => (
            <NarrativeListItem key={i}>{s}</NarrativeListItem>
          ))}
        </NarrativeList>
      ) : (
        <NarrativeBody>{cleaned}</NarrativeBody>
      )}
    </Section>
  );
};

const CollaborationHeadlinesSkeleton = () => (
  <>
    <Section>
      <SkeletonBar $w="140px" $h="22px" />
      <SkeletonBar $w="100%" $h="120px" style={{ marginTop: 16, borderRadius: 12 }} />
    </Section>
    <Section>
      <SkeletonBar $w="180px" $h="22px" />
      <CollabStatusRow>
        {[1, 2, 3, 4].map((i) => (
          <CollabStatusItem key={i}>
            <SkeletonBar $w="80px" $h="80px" style={{ borderRadius: 12 }} />
            <CollabStatusText>
              <SkeletonBar $w="140px" $h="22px" />
              <SkeletonBar $w="80px" $h="14px" style={{ marginTop: 6 }} />
            </CollabStatusText>
          </CollabStatusItem>
        ))}
      </CollabStatusRow>
    </Section>
    <TwoColumn>
      {[0, 1].map((col) => (
        <Section key={col}>
          <SkeletonBar $w="80px" $h="22px" />
          <SkeletonBar $w="95%" $h="14px" style={{ marginTop: 12 }} />
          <SkeletonBar $w="85%" $h="14px" style={{ marginTop: 6 }} />
        </Section>
      ))}
    </TwoColumn>
    <Section>
      <SkeletonBar $w="100px" $h="22px" />
      <SkeletonBar $w="90%" $h="14px" style={{ marginTop: 12 }} />
    </Section>
  </>
);

const skeletonPulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 0.3; }
  100% { opacity: 0.6; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 32px;
  background-color: ${colors.milkGlass200};
  min-height: calc(100vh - 140px);
`;

const DateFilterGroup = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 300px;
`;

const DateRangeNote = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${colors.milkGlass950};
  margin-top: 4px;
`;

const SessionsSection = styled.div`
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  margin: 0 16px;
`;

const SessionsHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  text-align: left;
`;

const SessionsBody = styled.div`
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DebugPanel = styled.div`
  margin: 0 16px;
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DebugPanelTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.nightlyWoods500};
`;

const DebugBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DebugBlockLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.milkGlass950};
`;

const DebugPre = styled.pre`
  margin: 0;
  padding: 12px;
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.milkGlass600};
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.nightlyWoods500};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
`;

const CardShell = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0px 1px 3px rgba(14, 26, 49, 0.1), 0px 1px 2px rgba(14, 26, 49, 0.06);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const StarsCountPill = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${colors.blue50};
  color: ${colors.blue500};
  border: 1px solid ${colors.blue300};
  border-radius: 15px;
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.25px;
`;

const CollabStatusRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px 16px;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const CollabStatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 220px;
  min-width: 220px;
`;

const BadgeWrapper = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > div {
    width: 80px;
    height: 80px;
  }
`;

const CollabStatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${colors.nightlyWoods500};
  min-width: 0;
`;

const CollabStatusLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  white-space: pre-line;
`;

const CollabStatusCount = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 8px 0 4px;
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 100px;
  border: 1px solid ${colors.nightlyWoods500};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: ${colors.milkGlass200};
  }

  &:active {
    opacity: 0.5;
  }
`;

const StarCard = styled.div`
  flex: 1;
  background: transparent;
  border-radius: 12px;
  padding: 32px;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
`;

const StarCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StarStudentName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const StarText = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const IncidentCard = styled.div`
  flex: 1;
  background: transparent;
  border-radius: 12px;
  padding: 24px 32px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IncidentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IncidentStudentName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
`;

const IncidentQuestionLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.milkGlass950};
`;

const IncidentQuote = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  font-style: italic;
  margin: 0;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const NarrativeBody = styled.p`
  margin: 0;
  padding: 0 24px 0 48px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const NarrativeList = styled.ul`
  margin: 0;
  padding: 0 24px 0 68px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  list-style-type: disc;
  list-style-position: outside;
`;

const NarrativeListItem = styled.li`
  display: list-item;
  margin-bottom: 4px;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 48px;
  padding: 0 16px;
  background: none;
  border: none;
  border-radius: 6px;
  color: ${colors.blue500};
  font-family: "Inter", sans-serif;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background: ${colors.blue50};
  }
`;

const ErrorBanner = styled.div`
  padding: 16px;
  background: ${colors.orange50};
  color: ${colors.orange700};
  border-radius: 6px;
  border: 1px solid ${colors.orange300};
  font-family: "Inter", sans-serif;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 64px 16px;
  text-align: center;
  color: ${colors.milkGlass950};
  font-family: "Inter", sans-serif;
  font-size: 16px;
`;

const SkeletonBar = styled.div`
  width: ${(p) => p.$w || "60px"};
  height: ${(p) => p.$h || "14px"};
  border-radius: 4px;
  background: ${colors.milkGlass600};
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;
